/**
 * An edit to a paired document, delivered when typing has paused (RFC 0301 S6).
 *
 * The app that made the edit calls this once the person stops. It is the comment
 * path again: find the sessions paired to the document, ask the gate, fire a
 * turn or record the edit behind one already queued. What it adds is the two
 * rules an edit needs and a comment does not:
 *
 *  - WHITESPACE ONLY STARTS NOTHING. Reflowing a paragraph is not a thought.
 *  - AN EDIT A SESSION MADE NEVER WAKES THAT SESSION (§3g). The caller passes
 *    only what its own window wrote, so an agent's patch arriving by sync is
 *    never offered; `authorSessionId` covers the rest.
 *
 * Every paired session hears it. Unlike a comment there is nothing to answer
 * exactly once, and each session is free to pass.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { Automerge } from '@automerge/automerge-repo/slim'
import { BranchLog } from '../../log/branch-log'
import { gateSignal } from '../../model/session/pairing-gate'
import { documentPairings } from '../../model/session/pairings'
import { projectSessionFirings } from '../../model/session/reads'
import { enqueueFiring } from '../run/enqueue-run'

/** How much of a diff rides on the log. The document is a read away. */
export const EDIT_DIFF_MAX = 2000

export type EditDelivery =
  /** `entryId` is the signal on the session's log, which is what a later
   *  keystroke withdraws (`withdrawEdit`). */
  | { sessionId: string; outcome: 'fired' | 'behind-queued'; entryId: string }
  | { sessionId: string; outcome: 'dropped'; drop: string }

export interface OfferEditResult {
  delivered: EditDelivery[]
  /** Why nothing was offered at all. */
  reason?: 'whitespace' | 'unchanged' | 'nobody-paired'
}

/**
 * True when this repo wrote any of the changes between `before` and `after`.
 *
 * BY ACTOR, NOT BY `patchInfo.source`. The source reads `change` for a merge
 * applied through `handle.update` as well as for typing, which a test caught:
 * an agent's patch landing that way would have woken its own session. Every
 * change carries the actor that made it, and a document loaded in this window
 * writes as this window's actor, so an arriving change never matches.
 */
export function isLocalChange(before: unknown, after: unknown): boolean {
  if (!before || !after) return false
  try {
    const mine = Automerge.getActorId(after as Automerge.Doc<unknown>)
    return Automerge.getChanges(
      before as Automerge.Doc<unknown>,
      after as Automerge.Doc<unknown>,
    ).some((c) => Automerge.decodeChange(c).actor === mine)
  } catch {
    // A snapshot this cannot read is not evidence of typing.
    return false
  }
}

/** Same words, ignoring how they are spaced. */
export function isWhitespaceOnly(before: string, after: string): boolean {
  return before.replace(/\s+/g, '') === after.replace(/\s+/g, '')
}

/**
 * The lines that changed, as `- removed` and `+ added`, with the unchanged head
 * and tail trimmed away. Not a minimal diff: one edited region per pause is the
 * common case, and a reader needs to see what moved, not an edit script.
 */
export function summariseEdit(before: string, after: string): string {
  const a = before.split('\n')
  const b = after.split('\n')
  let head = 0
  while (head < a.length && head < b.length && a[head] === b[head]) head++
  let tail = 0
  while (
    tail < a.length - head &&
    tail < b.length - head &&
    a[a.length - 1 - tail] === b[b.length - 1 - tail]
  ) {
    tail++
  }
  const removed = a.slice(head, a.length - tail).map((l) => `- ${l}`)
  const added = b.slice(head, b.length - tail).map((l) => `+ ${l}`)
  const body = [...removed, ...added].join('\n')
  return body.length > EDIT_DIFF_MAX
    ? `${body.slice(0, EDIT_DIFF_MAX - 1)}…`
    : body
}

export async function offerEdit(
  repo: Repo,
  projectId: string,
  opts: {
    documentId: string
    before: string
    after: string
    /** The passage selected when the person paused, if it is in this document. */
    selectionLabel?: string
    /** Set when a session made the edit. It is never offered its own write. */
    authorSessionId?: string
  },
): Promise<OfferEditResult> {
  if (opts.before === opts.after) return { delivered: [], reason: 'unchanged' }
  if (isWhitespaceOnly(opts.before, opts.after)) {
    return { delivered: [], reason: 'whitespace' }
  }

  // Off the document (RFC 0339 3b), and whatever the project still holds.
  const paired = (
    await documentPairings(repo, projectId, opts.documentId)
  ).filter((p) => p.sessionId !== opts.authorSessionId)
  if (paired.length === 0) return { delivered: [], reason: 'nobody-paired' }

  const diff = summariseEdit(opts.before, opts.after)
  const delivered: EditDelivery[] = []
  for (const pairing of paired) {
    const session = await repo.find(pairing.sessionId as AnyDocumentId)
    await (session as { whenReady?: () => Promise<void> }).whenReady?.()
    let entryId = ''
    const append = (log: BranchLog) => {
      entryId = log.appendEdited({
        documentId: opts.documentId,
        diff,
        ...(opts.selectionLabel ? { selectionLabel: opts.selectionLabel } : {}),
      })
      return entryId
    }
    const verdict = gateSignal(
      'edit',
      pairing,
      projectSessionFirings(session.doc()),
    )
    if (verdict.pass) {
      await enqueueFiring(
        repo,
        pairing.sessionId,
        { pairingId: pairing.id, documentId: opts.documentId, signal: 'edit' },
        append,
        { projectId },
      )
      delivered.push({
        sessionId: pairing.sessionId,
        outcome: 'fired',
        entryId,
      })
    } else if (verdict.drop === 'queued') {
      // The queued turn has not read anything yet, and will read this last.
      append(new BranchLog(session as never))
      delivered.push({
        sessionId: pairing.sessionId,
        outcome: 'behind-queued',
        entryId,
      })
    } else {
      delivered.push({
        sessionId: pairing.sessionId,
        outcome: 'dropped',
        drop: verdict.drop,
      })
    }
  }
  return { delivered }
}
